export function getOrderDocuments(order) {
  const source = order?.source || order || {};
  const files = [];
  const seen = new Set();
  const visit = (value, group = "Documents") => {
    if (!value || typeof value !== "object") return;
    if (Array.isArray(value)) {
      value.forEach((file) => visit(file, group));
      return;
    }
    const link = value.url || value.api_url;
    if (typeof link === "string") {
      let url;
      try { url = new URL(link, window.location.origin); } catch { return; }
      const category = value.collection_type || group;
      const key = `${category}:${url.href}`;
      if (!["https:", "http:"].includes(url.protocol) || seen.has(key)) return;
      seen.add(key);
      let fallbackName = "Document";
      try { fallbackName = decodeURIComponent(url.pathname.split("/").pop()) || fallbackName; } catch { /* Keep the fallback for malformed filenames. */ }
      files.push({ url: url.href, name: value.display_name || value.file_name || value.name || fallbackName, group: category });
      return;
    }
    Object.entries(value).forEach(([key, child]) => visit(child, key));
  };
  [source.attachments, source.documents, source.media].forEach((group) => visit(group));
  Object.entries(source).forEach(([key, value]) => {
    if (Array.isArray(value) && value.some((file) => file?.collection_type || file?.display_name)) visit(value, key);
  });
  return files;
}

// The signed agreement comes back under a few different collection names, so
// match the shape of the name rather than one exact spelling.
export const isAgreementGroup = (group) =>
  typeof group === "string" && /(^|_)spa$|purchase[_-]?agreement/i.test(group);

export function getSignedPurchaseDocuments(order, local = {}) {
  const documents = getOrderDocuments(order);
  const localDocument = (file, group) => file ? { file, name: file.name, group } : null;
  return {
    spa: localDocument(local.spa, "sales_purchase_agreement") || documents.find((file) =>
      isAgreementGroup(file.group)),
    vso: localDocument(local.vso, "vso_with_sign") || documents.find((file) =>
      ["vso_with_sign", "vso_document", "signed_vso"].includes(file.group)),
  };
}

const categoryLabels = {
  sales_purchase_agreement: "Signed Sale and Purchase Agreement",
  sale_purchase_agreement: "Signed Sale and Purchase Agreement",
  buyer_sale_purchase_agreement: "Signed Sale and Purchase Agreement",
  signed_sale_purchase_agreement: "Signed Sale and Purchase Agreement",
  spa: "Signed Sale and Purchase Agreement",
  vso_with_sign: "Signed Vehicle Sales Order (VSO)",
  id_front: "Identity Document (Front)",
  id_back: "Identity Document (Back)",
  license_front: "Driving Licence (Front)",
  license_back: "Driving Licence (Back)",
  company_ssm: "Company SSM",
  ssm: "SSM",
  vso: "Vehicle Sales Order (VSO)",
  do: "Delivery Order (DO)",
  buyer: "Buyer Sale and Purchase Agreement",
  buyer_agreement: "Buyer Sale and Purchase Agreement",
  service_agreement: "Service Agreement",
  ncd_letter: "NCD Letter",
  receipt: "Official Receipt",
  official_receipt: "Official Receipt",
  payment_receipt: "Official Receipt",
  geran: "Vehicle Registration (Geran)",
};

export function groupOrderDocuments(documents) {
  const groups = new Map();
  documents.forEach((file) => {
    const key = file.group || "Documents";
    if (!groups.has(key)) groups.set(key, {
      key,
      label: categoryLabels[key] || key.replace(/[_-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase()),
      files: [],
    });
    groups.get(key).files.push(file);
  });
  return Array.from(groups.values());
}
